import React, { useState, useEffect } from "react";
import { Loader, Checkbox, Button, Icon, Popup } from "semantic-ui-react";
import { useParams, useHistory } from "react-router-dom";
import { forEach, filter, size, every, map, includes, reject, minBy } from "lodash";
import moment from "moment";
import {
  HeaderPage,
  GuestsModal,
  CustomerSelector,
  DeliveryInfo,
} from "../../components/Admin";
import { ModalBasic, ModalConfirm, PaymentMethodModal } from "../../components/Common";
import {
  ListOrderAdmin,
  PaymentDetail,
} from "../../components/Admin/TableDetails";
import { useOrder, useTable, usePayment, useSiteConfig, usePolling, useInvoicing } from "../../hooks";
import { cleanProductCartApi } from "../../api/cart";
import { ORDER_STATUS } from "../../utils/constants";
import { getTablePlace } from "../../utils/table";
import { getOrderPrice, isBatchKind } from "../../utils/order";
import { formatElapsed } from "../../utils/date";
import { printTicket } from "../../utils/ticket";
import "./TableDetailsAdmin.scss";

// También se usa incrustado en Caja → Cobrar (`embedded`, con `tableId`): ahí
// "Cobrar" genera la cuenta y abre "Cobrar cuenta" en un solo paso, y al cobrar
// o liberar la mesa se avisa con `onClosed` en vez de navegar a Pedidos.
export function TableDetailsAdmin(props) {
  const { tableId, embedded = false, onClosed } = props;
  const [reloadOrders, setReloadOrders] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const params = useParams();
  const id = tableId ?? params.id;
  const history = useHistory();
  const {
    loading,
    orders,
    getOrdersByTable,
    addPaymentToOrder,
    closeOrder,
    deleteOrder,
  } = useOrder();
  const { table, getTable, setTableGuests, setTableCustomer, setTableDelivery } = useTable();
  const [showGuests, setShowGuests] = useState(false);
  const { siteConfig, getSiteConfig } = useSiteConfig();

  // Nombre del negocio para el encabezado de la pre-cuenta.
  useEffect(() => {
    getSiteConfig();
  }, []);
  // ¿Hay facturación activa? Entonces "Generar cuenta" pregunta el comprobante.
  const { issuer, getIssuer } = useInvoicing();
  useEffect(() => {
    getIssuer();
  }, []);
  const {
    createPayment,
    getPaymentByTable,
    setPaymentGuests,
    setPaymentCustomer,
    setPaymentDelivery,
  } = usePayment();

  const [showModal, setShowModal] = useState(false);
  const [showTypePayment, setShowTypePayment] = useState(false);
  const [showReleaseTable, setShowReleaseTable] = useState(false);
  // Pendientes que el Trabajador DESMARCÓ ("cocina no lo preparó") al liberar
  // la mesa. Se guardan los desmarcados (no los marcados) para que, por
  // defecto, todo pendiente —incluso uno que llegue con el refresco de 10s
  // mientras el modal está abierto— cuente como pérdida (opción B).
  const [notPreparedIds, setNotPreparedIds] = useState([]);

  useEffect(() => {
    getOrdersByTable(id, "", "ordering=-status,created_at");
  }, [id, reloadOrders]);

  useEffect(() => {
    getTable(id);
  }, [id]);

  useEffect(() => {
    (async () => {
      const response = await getPaymentByTable(id);
      setPaymentData(size(response) > 0 ? response[0] : null);
    })();
  }, [id, reloadOrders]);

  // Incrustado, al pasar a otra mesa se cierra lo que quedó abierto de la anterior.
  useEffect(() => {
    setShowModal(false);
    setPaymentData(null);
  }, [id]);

  // Refresco cada 10 s con usePolling (no setInterval): en el celular del
  // mozo (iPhone/iPad) los timers se congelaban y la mesa quedaba
  // desactualizada; así también refresca al volver a la app o al tocarla.
  usePolling(() => {
    getOrdersByTable(id, "", "ordering=-status,created_at", true);
    (async () => {
      const response = await getPaymentByTable(id);
      setPaymentData(size(response) > 0 ? response[0] : null);
    })();
  }, 10000);

  const allDelivered =
    size(orders) > 0 &&
    every(orders, (order) => order.status === ORDER_STATUS.DELIVERED);

  const pendingOrders = filter(
    orders,
    (order) => order.status === ORDER_STATUS.PENDING
  );
  // Entregados y "Listo" (cocina ya lo preparó): al liberar la mesa son
  // pérdida segura, sin preguntar.
  const deliveredOrders = filter(
    orders,
    (order) =>
      order.status === ORDER_STATUS.DELIVERED ||
      order.status === ORDER_STATUS.READY
  );
  // "Liberar mesa": entregados + pendientes que cocina ya preparó = pérdida;
  // los pendientes desmarcados se cancelan (se borran). Antes TODO pendiente
  // se borraba sin rastro, aunque cocina ya lo hubiera preparado — esa
  // pérdida real no aparecía en el Reporte de Pérdidas.
  const preparedPendingOrders = reject(pendingOrders, (order) =>
    includes(notPreparedIds, order.id)
  );
  const cancelledPendingOrders = filter(pendingOrders, (order) =>
    includes(notPreparedIds, order.id)
  );
  const lossOrders = [...deliveredOrders, ...preparedPendingOrders];
  const lossTotal = lossOrders
    .reduce((acc, order) => acc + getOrderPrice(order), 0)
    .toFixed(2);

  const togglePrepared = (orderId) =>
    setNotPreparedIds((prev) =>
      includes(prev, orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );

  const openReleaseTable = () => {
    setNotPreparedIds([]);
    setShowReleaseTable(true);
  };

  const releaseTableContent = () => (
    <div className="release-table">
      {size(deliveredOrders) > 0 && (
        <p>
          {size(deliveredOrders)} producto(s) ya entregado(s) o listo(s) en
          cocina se registrarán como pérdida.
        </p>
      )}

      {size(pendingOrders) > 0 && (
        <>
          <p>
            <strong>Pedidos pendientes</strong> — marca los que cocina{" "}
            <strong>ya preparó</strong> (cuentan como pérdida); los desmarcados
            se cancelan:
          </p>
          {map(pendingOrders, (order) => (
            <div key={order.id} className="release-table__item">
              <Checkbox
                checked={!includes(notPreparedIds, order.id)}
                onChange={() => togglePrepared(order.id)}
                label={`${order.product_data?.title || "Producto eliminado"} — S/ ${getOrderPrice(order).toFixed(2)}`}
              />
            </div>
          ))}
        </>
      )}

      <p className="release-table__total">
        Pérdida a registrar: <strong>S/ {lossTotal}</strong>
        {size(cancelledPendingOrders) > 0 &&
          ` · ${size(cancelledPendingOrders)} pedido(s) se cancelarán`}
      </p>
    </div>
  );

  const onReloadOrders = () => setReloadOrders((prev) => !prev);

  // Personas: mientras no hay cuenta viven en la mesa (current_guests); al
  // generar la cuenta pasan a ella (Payment.guests).
  const isSalon = !table?.kind || table.kind === "SALON";
  const guests = paymentData ? paymentData.guests : table?.current_guests;
  // Mesero que atiende el grupo (mesa → cuenta, mismo ciclo que las personas).
  const waiter = paymentData ? paymentData.waiter_name : table?.current_waiter;
  const waiterId = paymentData ? paymentData.waiter_user : table?.current_waiter_user;
  // Delivery: canal y envío, mismo ciclo (mesa → cuenta).
  const isDelivery = table?.kind === "DELIVERY";
  const deliveryChannel = paymentData
    ? paymentData.delivery_channel
    : table?.current_delivery_channel;
  const deliveryFee = paymentData ? paymentData.delivery_fee : table?.current_delivery_fee;
  const onChangeDelivery = async (channel, fee) => {
    if (paymentData) setPaymentData(await setPaymentDelivery(paymentData.id, channel, fee));
    else {
      await setTableDelivery(id, channel, fee);
      getTable(id);
    }
  };

  // Cliente: mismo ciclo que las personas (mesa → cuenta).
  const customer = paymentData ? paymentData.customer_data : table?.current_customer_data;
  const onChangeCustomer = async (selected) => {
    const customerId = selected ? selected.id : null;
    if (paymentData) setPaymentData(await setPaymentCustomer(paymentData.id, customerId));
    else {
      await setTableCustomer(id, customerId);
      getTable(id);
    }
  };

  const onConfirmGuests = async (count, waiterUserId) => {
    if (paymentData) setPaymentData(await setPaymentGuests(paymentData.id, count, waiterUserId));
    else {
      await setTableGuests(id, count, waiterUserId);
      getTable(id);
    }
    setShowGuests(false);
  };
  const openCloseModal = () => setShowModal((prev) => !prev);
  const goToAddOrder = () => {
    cleanProductCartApi();
    history.push(`/admin/table/${id}/menu`);
  };

  const onCreatePayment = async (paymentType, { quiet = false, docType = "" } = {}) => {
    setShowTypePayment(false);

    const existingPayment = await getPaymentByTable(id);
    if (size(existingPayment) > 0) {
      if (!quiet) {
        window.alert(
          "Esta mesa ya tiene una cuenta generada. La pantalla se va a actualizar."
        );
      }
      setPaymentData(existingPayment[0]);
      onReloadOrders();
      return existingPayment[0];
    }

    let totalPayment = 0;
    forEach(orders, (order) => {
      totalPayment += getOrderPrice(order);
    });

    const paymentData = {
      table: id,
      totalPayment: totalPayment.toFixed(2),
      paymentType,
      statusPayment: "PENDING",
      // Comprobante que pidió el cliente (la Caja lo ve ya elegido).
      ...(docType === "BOLETA" || docType === "FACTURA" ? { requested_doc_type: docType } : {}),
    };

    const payment = await createPayment(paymentData);

    for await (const order of orders) {
      await addPaymentToOrder(order.id, payment.id);
    }
    onReloadOrders();
    return payment;
  };

  // Caja → Cobrar: un solo botón. Si la mesa aún no pidió la cuenta, se genera
  // (en efectivo; la forma de pago se cambia en "Cobrar cuenta") y se abre.
  const onCharge = async () => {
    if (!paymentData) {
      const payment = await onCreatePayment("CASH", { quiet: true });
      if (!payment) return;
      setPaymentData(payment);
    }
    setShowModal(true);
  };
  const occupiedSince = minBy(orders, (order) => moment(order.created_at).valueOf())?.created_at;
  const toCharge = paymentData
    ? Number(paymentData.totalPayment)
    : (orders || []).reduce((acc, order) => acc + getOrderPrice(order), 0) +
      (isDelivery ? Number(deliveryFee || 0) : 0);
  const onTableClosed = embedded ? onClosed : () => history.push("/admin");

  const onReleaseTable = async () => {
    setShowReleaseTable(false);

    for await (const order of cancelledPendingOrders) {
      await deleteOrder(order.id, "TABLE_RELEASED");
    }

    if (size(lossOrders) > 0) {
      const lossPaymentData = {
        table: id,
        totalPayment: lossTotal,
        paymentType: "LOSS",
        statusPayment: "PAID",
      };

      const payment = await createPayment(lossPaymentData);

      for await (const order of lossOrders) {
        await addPaymentToOrder(order.id, payment.id);
        await closeOrder(order.id);
      }
    }

    onReloadOrders();
    if (embedded) onClosed?.();
  };

  return (
    <>
      {embedded ? (
        // Caja: cobra y libera; los pedidos los añade el mozo desde la mesa.
        <HeaderPage
          title={table ? getTablePlace(table) : "Mesa"}
          btnTitleTwo="Cobrar"
          btnTitleTwoDisabled={!paymentData && !allDelivered}
          btnClickTwo={onCharge}
          btnTitleThree={!paymentData && size(orders) > 0 ? "Liberar mesa" : null}
          btnClickThree={openReleaseTable}
        />
      ) : (
        <HeaderPage
          title={table ? getTablePlace(table) : "Mesa"}
          btnTitle={paymentData ? "Ver cuenta" : "Añadir pedido"}
          btnClick={paymentData ? openCloseModal : goToAddOrder}
          btnTitleTwo={!paymentData ? "Generar cuenta" : null}
          btnTitleTwoDisabled={!allDelivered}
          btnClickTwo={() => setShowTypePayment(true)}
        />
      )}
      {embedded && !paymentData && size(orders) > 0 && !allDelivered && (
        <p className="table-details-admin__hint">
          Para cobrar, todos los platos deben estar entregados.
        </p>
      )}
      {loading ? (
        <Loader active inline="centered">
          Cargando...
        </Loader>
      ) : (
        <>
          <div className="table-details-admin__actions">
              {isDelivery && (
                <DeliveryInfo
                  title={table ? getTablePlace(table) : "Delivery"}
                  channel={deliveryChannel}
                  fee={deliveryFee}
                  onChange={onChangeDelivery}
                />
              )}
              <CustomerSelector
                customer={customer}
                onChange={onChangeCustomer}
                attention={!embedded}
              />
              {/* Personas en la mesa (salón): se marca al abrirla desde
                  "Pedidos"; aquí se ve y se corrige (o se marca si la abrió el
                  comensal con el QR). */}
              {isSalon && (
                <Button
                  basic
                  className={!guests && !embedded ? "attention-button" : undefined}
                  onClick={() => setShowGuests(true)}
                >
                  <Icon name="users" />{" "}
                  {guests ? `${guests} persona${guests > 1 ? "s" : ""}` : "¿Cuántas personas?"}
                  {guests && waiter ? ` · ${waiter}` : ""}
                </Button>
              )}
              {size(orders) > 0 && (
                <Button
                  basic
                  // Texto de la pre-cuenta en Restaurant.pe (video, 2026-10-11).
                  title="Para entregar al cliente y verifique lo consumido."
                  onClick={() =>
                    printTicket({
                      siteName: siteConfig?.site_name,
                      orders,
                      guests,
                      waiter,
                      customer,
                      deliveryFee: isDelivery ? deliveryFee : null,
                      deliveryChannel: isDelivery ? deliveryChannel : null,
                    })
                  }
                >
                  <Icon name="print" /> Imprimir pre-cuenta
                </Button>
              )}
              {/* El aviso nativo (`title`) tarda en salir con el mouse y no existe en
                  el teléfono: este ícono lo muestra al pasar el cursor o al tocarlo. */}
              {size(orders) > 0 && (
                <Popup
                  trigger={
                    <Icon
                      name="info circle"
                      link
                      color="grey"
                      aria-label="Para qué sirve la pre-cuenta"
                      style={{ alignSelf: "center" }}
                    />
                  }
                  content="Pre-cuenta: para entregar al cliente y verifique lo consumido."
                  on={["hover", "click"]}
                  // El ícono queda al borde derecho: se abre hacia la izquierda.
                  position="bottom right"
                  size="small"
                />
              )}
          </div>
          <ListOrderAdmin
            orders={orders}
            onReloadOrders={onReloadOrders}
            batchMode={isBatchKind(table?.kind)}
          />
          {/* Resumen al pie, como en Restaurant.pe: ocupada desde · pedidos · por pagar. */}
          {embedded && size(orders) > 0 && (
            <div className="table-details-admin__summary">
              <div>
                <Icon name="clock outline" />
                <span>
                  <strong>{formatElapsed(occupiedSince).replace("hace ", "")}</strong>
                  ocupada
                </span>
              </div>
              <div>
                <Icon name="file alternate outline" />
                <span>
                  <strong>{size(orders)}</strong>
                  pedido{size(orders) > 1 ? "s" : ""}
                </span>
              </div>
              <div>
                <Icon name="money bill alternate outline" />
                <span>
                  <strong>S/ {toCharge.toFixed(2)}</strong>
                  por pagar
                </span>
              </div>
            </div>
          )}
        </>
      )}

      {paymentData && (
        <ModalBasic
          show={showModal}
          onClose={openCloseModal}
          title={embedded ? "Cobrar cuenta" : "Cuenta"}
        >
          <PaymentDetail
            // Desde la mesa (el mozo) solo se ve: se cobra en Caja → Cobrar.
            readOnly={!embedded}
            payment={paymentData}
            orders={orders}
            openCloseModal={openCloseModal}
            onReloadOrders={onReloadOrders}
            onTableClosed={onTableClosed}
          />
        </ModalBasic>
      )}

      <GuestsModal
        show={showGuests}
        title={table ? getTablePlace(table) : "Mesa"}
        initialValue={guests}
        initialWaiterId={waiterId}
        onCancel={() => setShowGuests(false)}
        onConfirm={onConfirmGuests}
      />

      <PaymentMethodModal
        show={showTypePayment}
        askDocument={!!issuer?.enabled}
        customer={customer}
        onSelect={(method, docType) => onCreatePayment(method, { docType })}
        onCancel={() => setShowTypePayment(false)}
      />

      <ModalConfirm
        title="¿Liberar esta mesa?"
        content={releaseTableContent()}
        show={showReleaseTable}
        onCloseText="Cancelar"
        onClose={() => setShowReleaseTable(false)}
        onConfirmText="Liberar mesa"
        onConfirm={onReleaseTable}
      />
    </>
  );
}
